import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [deliveryMode, setDeliveryMode] = useState(null);
  const [currentSlug, setCurrentSlug] = useState(null);
  // routePrefix: '/${slug}' en modo path-based, '' en modo subdominio/dominio personalizado
  const [routePrefix, setRoutePrefix] = useState('');

  useEffect(() => {
    if (currentSlug) {
      const savedCart = localStorage.getItem(`cart_${currentSlug}`);
      const savedMode = localStorage.getItem(`mode_${currentSlug}`);
      if (savedCart) setCartItems(JSON.parse(savedCart));
      if (savedMode) setDeliveryMode(savedMode);
    }
  }, [currentSlug]);

  useEffect(() => {
    if (currentSlug && cartItems.length > 0) {
      localStorage.setItem(`cart_${currentSlug}`, JSON.stringify(cartItems));
    }
  }, [cartItems, currentSlug]);

  useEffect(() => {
    if (currentSlug && deliveryMode) {
      localStorage.setItem(`mode_${currentSlug}`, deliveryMode);
    }
  }, [deliveryMode, currentSlug]);

  const setSlug = (slug) => {
    if (slug !== currentSlug) {
      if (currentSlug && currentSlug !== slug) {
        setCartItems([]);
        setDeliveryMode(null);
      }
      setCurrentSlug(slug);
    }
  };

  const addToCart = (product) => {
    setCartItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      const currentQty = existing ? existing.quantity : 0;
      if (product.stock != null && currentQty >= product.stock) return prev;
      if (existing) return prev.map((i) => i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId) => setCartItems((prev) => prev.filter((i) => i.id !== productId));

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) { removeFromCart(productId); return; }
    const item = cartItems.find((i) => i.id === productId);
    if (item && item.stock != null && quantity > item.stock) return;
    setCartItems((prev) => prev.map((i) => i.id === productId ? { ...i, quantity } : i));
  };

  const clearCart = () => {
    setCartItems([]);
    setDeliveryMode(null);
    if (currentSlug) {
      localStorage.removeItem(`cart_${currentSlug}`);
      localStorage.removeItem(`mode_${currentSlug}`);
    }
  };

  const getTotal = () => cartItems.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const getItemCount = () => cartItems.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{
      cartItems, deliveryMode, setDeliveryMode,
      addToCart, removeFromCart, updateQuantity, clearCart,
      getTotal, getItemCount, setSlug,
      routePrefix, setRoutePrefix,
    }}>
      {children}
    </CartContext.Provider>
  );
};
